import type { FC, ReactNode } from 'react'
import type { Wallpaper } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, Monitor, Smartphone } from 'lucide-react'
import { pluralize } from '@/lib/format'

interface DeviceSplitProps {
    desktopCount: number
    phoneCount: number
    desktopWallpaper?: Wallpaper
    phoneWallpaper?: Wallpaper
}

interface DeviceTileProps {
    href: string
    icon: ReactNode
    title: string
    count: number
    wallpaper?: Wallpaper
}

const DeviceTile: FC<DeviceTileProps> = ({ href, icon, title, count, wallpaper }): ReactNode => {
    return (
        <Link
            href={href}
            className='group relative isolate flex min-h-72 flex-col justify-end overflow-hidden rounded-3xl bg-surface p-6 ring-1 ring-white/[0.06] focus-visible:ring-3 focus-visible:ring-ring/70 focus-visible:outline-none sm:min-h-80 sm:p-8'
        >
            {wallpaper && (
                <Image
                    src={wallpaper.image}
                    alt=''
                    fill
                    sizes='(min-width: 768px) 50vw, 100vw'
                    className='-z-20 object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                />
            )}

            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-t from-black/85 via-black/40 to-black/5' />

            <span className='grid size-11 place-items-center rounded-2xl bg-white/15 text-white backdrop-blur-md'>
                {icon}
            </span>

            <h3 className='mt-5 font-heading text-4xl text-white sm:text-5xl'>
                {title}
            </h3>

            <p className='mt-2 flex items-center gap-2 text-sm font-medium text-white/80'>
                <span>
                    <span>{count}</span>
                    {' '}
                    {pluralize(count, 'wallpaper')}
                </span>

                <ArrowRight aria-hidden='true' className='size-4 transition-transform group-hover:translate-x-1 motion-reduce:group-hover:translate-x-0' />
            </p>
        </Link>
    )
}

export const DeviceSplit: FC<DeviceSplitProps> = ({ desktopCount, phoneCount, desktopWallpaper, phoneWallpaper }): ReactNode => {
    return (
        <div className='grid gap-4 md:grid-cols-2'>
            <DeviceTile
                href='/wallpapers?device=desktop'
                icon={<Monitor aria-hidden='true' className='size-5' />}
                title='For your desktop'
                count={desktopCount}
                wallpaper={desktopWallpaper}
            />

            <DeviceTile
                href='/wallpapers?device=phone'
                icon={<Smartphone aria-hidden='true' className='size-5' />}
                title='For your phone'
                count={phoneCount}
                wallpaper={phoneWallpaper}
            />
        </div>
    )
}
